import { useUser } from '@hooks'
import { useFloor } from '@providers/mapProvider'
import { useCallback, useEffect, useReducer } from 'react'
import {
  SpacesWithIDType,
  filterViewFiltersForFloor,
  getByID,
  rawToFilters,
} from './viewFiltersCalculations'
import { normalizeSpaces } from '@api/floor/normalize'
import { useAppData } from '@providers'
import { get } from 'lodash'
import { ReturnTypeGetFilterById, ViewFilterChild } from '@api'
import update from '@api/ap/update'
import { updateContext } from '@api/user/update'

enum Actions {
  SAVE_FILTERS,
  TOGGLE_CHILD,
}
interface ISaveFilters {
  type: Actions.SAVE_FILTERS
  filters: ViewFilterState
}

interface IToggleChild {
  type: Actions.TOGGLE_CHILD
  id: string
}

type RootAction = ISaveFilters | IToggleChild

type Filters = {
  id: string
  key: string
  name: string
  parentId: string
  tooltip: string
  visible: boolean
}

type ViewFilterState = {
  byId: Record<string, Record<string, unknown>> | null
  dynamicFilterIds: ViewFilterChild[] | null
  staticFilterIds: ViewFilterChild[] | null
  filters: Filters[] | null
}

const reducer = (state: ViewFilterState, action: RootAction) => {
  switch (action.type) {
    case Actions.SAVE_FILTERS:
      return {
        ...state,
        ...action.filters,
      }
    case Actions.TOGGLE_CHILD:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.id]: {
            ...state?.byId?.[action.id],
            visible: !state?.byId?.[action.id].visible,
          },
        },
      }
    default:
      return state
  }
}
export default function useViewFilters() {
  const floor = useFloor?.()
  const user = useUser?.()

  const { product } = useAppData()

  const fetchViewFilters = useCallback(() => {
    if (!floor?.viewFilters) return {} as ViewFilterState
    const filteredViewFilters = filterViewFiltersForFloor({
      viewFilters: [...floor.viewFilters],
      spaces: Object.values(floor?.spaces?.byId),
    })

    const spaces = normalizeSpaces(Object.values(floor?.spaces?.byId))
    const byId = Object.keys(floor.spaces.byId).reduce((newResultById: any, id) => {
      newResultById[id] = floor.spaces.byId[id]
      return newResultById
    }, {})

    const spacesWithID = { ...spaces, byId } as SpacesWithIDType

    const rawData = rawToFilters({
      viewFilters: filteredViewFilters,
      context: user.user?.context,
      spaces: spacesWithID,
      product,
    })

    const filters = {
      byId: getByID(rawData.byId),
      filters: rawData.filterBlackList,
      dynamicFilterIds: rawData.dynamicFilterIds,
      staticFilterIds: rawData.staticFilterIds,
    }
    return filters
  }, [floor?.spaces?.byId, floor?.viewFilters, product, user?.user?.context])

  const [viewFilters, dispatch] = useReducer(reducer, {
    byId: null,
    dynamicFilterIds: null,
    staticFilterIds: null,
    filters: null,
  })

  useEffect(() => {
    const filters = fetchViewFilters()
    dispatch({ type: Actions.SAVE_FILTERS, filters })
  }, [fetchViewFilters])

  const getSingleFilterName = (id: string) => {
    if (viewFilters.byId) {
      const filter: { name: string } = Object.values(viewFilters.byId).find(
        ({ parentId, visible }: any) => parentId === id && visible
      ) as { name: string }
      return filter?.name
    } else return ''
  }

  const getFilterById = (id: string) => {
    const units = get(user, 'user.context.units', '')
    const isSafeDistancing = Boolean(floor?.map?.scale)
    if (viewFilters.byId) {
      return {
        ...viewFilters.byId[id],
        safeDistance: floor?.map?.safeDistance,
        units,
        isSafeDistancing,
      }
    } else {
      return {} as ReturnTypeGetFilterById
    }
  }

  const toggleChild = (id: string) => {
    dispatch({ type: Actions.TOGGLE_CHILD, id })
    saveFilters(id)
  }

  const saveFilters = (id: string) => {
    const { filterVisibilityMap } = getSaveData(viewFilters)
    updateContext(user.user?.id as string, {
      ...user.user?.context,
      filterVisibilityMap: {
        ...filterVisibilityMap,
        [id]: !viewFilters?.byId?.[id].visible,
      },
    })
  }

  return {
    viewFilters,
    getSingleFilterName,
    getFilterById,
    toggleChild,
    saveFilters,
  }
}

const getSaveData = (filters: any) => {
  return Object.keys(filters.byId).reduce(
    (data, id) => {
      const filter: any = filters.byId[id]
      if (filter.parentId) {
        data.filterVisibilityMap[id] = filter.visible
      }
      return data
    },
    { filterVisibilityMap: {} as any }
  )
}
